import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Chip, Link } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ButtonComponent from "../../../components/common/button/Button";
import { Loader } from "../../../components/common/Loader";
import { formatDate, formatDateTime } from "../../../components/common/FormatDate";
import {
  getForecastHistoryDownloadListEndpoint,
  getForecastHistoryExcelFileEndpoint,
  getBuyerForecastHistoryExportJobsEndpoint,
} from "../../../common/endpoints";
import { getForecastHistoryDownloadList } from "../../../redux/actions/getForecastHistoryDownloadListSlice";
import { getForecastHistorySearch } from "../../../redux/actions/getForecastHistorySearchSlice";

const StatusCellRenderer = (params) => {
  const status = params.value;
  const color =
    status === "COMPLETED" || status === "Downloaded"
      ? "#388e3c"
      : status === "QUEUED" || status === "IN_PROGRESS" || status === "Pending"
        ? "#f57c00"
        : status === "FAILED"
          ? "#d32f2f"
          : "#000";
  return (
    <Chip
      label={status || "-"}
      size="small"
      sx={{
        backgroundColor: color,
        color: "#fff",
        fontSize: 11,
        height: 22,
      }}
    />
  );
};

const MyDownloadsTab = ({ refreshTrigger }) => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const userRole = localStorage.getItem("userRole");
  const userEmail = localStorage.getItem("userEmail");
  const supplierMode = userRole === "Supplier Planner" || userRole === "Supplier Admin";
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const res = await dispatch(
        getForecastHistoryDownloadList({
          dynamicEndpoint: `${getBuyerForecastHistoryExportJobsEndpoint}/${encodeURIComponent(userEmail)}`,
          skipUserEmailParam: true,
        })
      );
      if (res?.meta?.requestStatus === "fulfilled") {
        const payload = res?.payload;
        const normalizedRows = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.content || payload?.items || [];
        setRowData(normalizedRows);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error fetching downloads list", e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    const success = await fetchDownloads();
    if (success) {
      toast.success("My Downloads refreshed", { autoClose: 1800 });
    } else {
      toast.error("Unable to refresh My Downloads", { autoClose: 2500 });
    }
    setRefreshing(false);
  };

  useEffect(() => {
    fetchDownloads();
  }, [dispatch, refreshTrigger]);

  const handleDownloadFile = async (row) => {
    const rowJobId = row?.jobId ?? row?.downloadId;
    const rowFileName = row?.fileName;
    const rowDownloadUrl = row?.downloadUrl;
    setDownloadingId(rowJobId);
    try {
      if (rowDownloadUrl) {
        const a = document.createElement("a");
        a.href = rowDownloadUrl;
        a.download = rowFileName || `forecast-history-${rowJobId}.csv`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.click();
        return;
      }

      if (!supplierMode) {
        const detailsRes = await dispatch(
          getForecastHistoryDownloadList({
            dynamicEndpoint: `${getBuyerForecastHistoryExportJobsEndpoint}/${encodeURIComponent(userEmail)}/${rowJobId}`,
            skipUserEmailParam: true,
          })
        );

        const detailsPayload = detailsRes?.payload?.data || detailsRes?.payload;
        const downloadUrl = detailsPayload?.downloadUrl;
        const fileName = detailsPayload?.fileName || rowFileName || `forecast-history-${rowJobId}.csv`;

        if (detailsRes?.meta?.requestStatus === "fulfilled" && downloadUrl) {
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = fileName;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.click();
        } else {
          toast.error("File not ready or download URL unavailable", { autoClose: 2500 });
        }
      } else {
        const res = await dispatch(
          getForecastHistorySearch({
            dynamicEndpoint: `${getForecastHistoryExcelFileEndpoint}/${rowJobId}`,
            responseType: "blob",
            skipUserEmailParam: true,
          })
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          const blob = new Blob([res.payload], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = rowFileName || `forecast-history-${rowJobId}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        } else {
          toast.error("File not ready or download failed", { autoClose: 2500 });
        }
      }
    } catch (e) {
      console.error("Download file error", e);
      toast.error("Error downloading file", { autoClose: 2500 });
    } finally {
      setDownloadingId(null);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Request Date & Time(UTC)",
        field: "requestedDateTime",
        flex: 2,
        minWidth: 200,
        sort: "desc",
        valueFormatter: (params) => formatDateTime(params.value),
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
              filterParams: {
                valueFormatter: (params) => formatDateTime(params.value),
                excelMode: "windows",
                buttons: ["apply", "reset"],
              },
            },
          ],
        },
      },
      {
        headerName: "From Date", field: "fromExtractionDate", flex: 1.2, minWidth: 140, valueFormatter: (params) => formatDate(params.value),
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
              filterParams: {
                valueFormatter: (params) => formatDate(params.value),
                excelMode: "windows",
                buttons: ["apply", "reset"],
              },
            },
          ],
        },
      },
      {
        headerName: "To Date", field: "toExtractionDate", flex: 1.2, minWidth: 140, valueFormatter: (params) => formatDate(params.value), filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
              filterParams: {
                valueFormatter: (params) => formatDate(params.value),
                excelMode: "windows",
                buttons: ["apply", "reset"],
              },
            },
          ],
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        minWidth: 140,
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: "Download",
        field: "jobId",
        flex: 1,
        minWidth: 140,
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const status = params?.data?.status;
          const isReady = status === "COMPLETED" || status === "Downloaded";
          const id = params?.data?.jobId ?? params?.data?.downloadId;
          const isBusy = downloadingId === id;

          if (!isReady) {
            return <Typography sx={{ fontSize: 12, color: "#8a8a8a" }}>-</Typography>;
          }

          return (
            <Link
              component="button"
              underline="always"
              onClick={() => handleDownloadFile(params?.data)}
              disabled={isBusy}
              sx={{
                fontSize: 14,
                fontWeight: 600,
                cursor: isBusy ? "not-allowed" : "pointer",
                color: isBusy ? "#8a8a8a" : "#1976d2",
                background: "none",
                border: "none",
                p: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                opacity: 1,
                "&:hover": {
                  color: isBusy ? "#8a8a8a" : "#0d47a1",
                  textDecoration: "underline",
                  background: "transparent",
                  opacity: 1,
                },
                "&:focus": {
                  color: isBusy ? "#8a8a8a" : "#0d47a1",
                  textDecoration: "underline",
                  opacity: 1,
                },
                "& .MuiSvgIcon-root": {
                  color: "inherit",
                },
              }}
            >
              <DownloadRoundedIcon sx={{ fontSize: 17 }} />
              {isBusy ? "Downloading..." : "Download"}
            </Link>
          );
        },
      },
    ],
    [downloadingId]
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agMultiColumnFilter", flex: 1, minWidth: 130 }),
    []
  );

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          mt: 1,
        }}
      >
        <Typography fontSize={14} fontWeight={600}>
          My Download Requests
        </Typography>
        <ButtonComponent
          type="primary"
          maxWidth="100px"
          label={refreshing ? "Loading" : "Refresh"}
          height="32px"
          onClick={handleRefresh}
          disabled={loading || refreshing}
        />
      </Box>

      <Box sx={{ position: "relative" }}>
        <div className="ag-theme-quartz" style={{ height: 420, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination
            paginationPageSize={20}
            suppressMovableColumns
          />
        </div>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 56,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <Loader />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MyDownloadsTab;
